"use client"

import { useState, useEffect } from "react"
import {
  FaSearch,
  FaFilter,
  FaDownload,
  FaEye,
  FaFileInvoice,
  FaCheckCircle,
  FaTimesCircle,
  FaClipboardList,
  FaMoneyBillWave,
} from "react-icons/fa"
import Card from "../admincomponents/Card"
import Table from "../admincomponents/Table"

const BillingPayments = () => {
  const [activeTab, setActiveTab] = useState("requests")
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [currentInvoice, setCurrentInvoice] = useState(null)
  const [paymentRequests, setPaymentRequests] = useState([])
  const [membershipData, setMembershipData] = useState({
    newRequests: [],
    waitingPayment: [],
    paymentDone: [],
    activeMemberships: [],
    rejectedRequests: []
  })
  const [loadingAction, setLoadingAction] = useState(null);
  const [loadingMembershipAction, setLoadingMembershipAction] = useState(null);
  const [isLoadingMemberships, setIsLoadingMemberships] = useState(false);

  // --- Fetch enrollment requests ---
  useEffect(() => {
    const fetchEnrollmentRequests = async () => {
      try {
        const token = localStorage.getItem('jwttoken');

        const response = await fetch("https://kaasbackend.vetrinahealthcare.com/lms/enrollment-requests/", {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (!response.ok) {
          throw new Error('Failed to fetch enrollment requests');
        }

        const data = await response.json()
        const transformedData = data.map((request) => ({
          id: request.request_id,
          user: request.full_name,
          email: request.email,
          course: request.course_title,
          amount: request.course_price,
          date: request.join_date ? new Date(request.join_date).toLocaleDateString() : 'N/A',
          status: request.status === "A" ? "Accepted" : request.status === "R" ? "Rejected" : "Pending",
        }))
        setPaymentRequests(transformedData)
      } catch (error) {
        console.error("Error fetching enrollment requests:", error)
      }
    }

    fetchEnrollmentRequests()
  }, [])

  // --- Fetch membership data when payments tab is active ---
  useEffect(() => {
    if (activeTab === "payments") {
      fetchMembershipData();
    }
  }, [activeTab]);

  // --- Fetch membership data ---
  const fetchMembershipData = async () => {
    try {
      setIsLoadingMemberships(true);
      const token = localStorage.getItem('jwttoken');
      
      const response = await fetch("https://kaasbackend.vetrinahealthcare.com/lms/membership/enrollment/pending/", {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch membership data: ${response.status}`);
      }

      const data = await response.json();
      
      // Transform API data for each category
      const transformMembershipItem = (item) => ({
        id: item.id,
        enrollmentId: item.id,
        userId: item.user,
        userFullName: item.user_full_name,
        userEmail: item.user_email,
        membershipId: item.membership,
        membershipTitle: item.membership_title,
        membershipType: item.membership_type,
        status: item.status, // Keep original status from API
        enrolledAt: item.enrolled_at,
        expiryDate: item.expiry_date,
        paymentReference: item.payment_reference,
        paymentDone: item.payment_done,
        date: item.enrolled_at ? new Date(item.enrolled_at).toLocaleDateString() : 'N/A',
        originalData: item
      });

      setMembershipData({
        newRequests: data.new_requests?.map(transformMembershipItem) || [],
        waitingPayment: data.waiting_payment?.map(transformMembershipItem) || [],
        paymentDone: data.payment_done?.map(transformMembershipItem) || [],
        activeMemberships: data.active_memberships?.map(transformMembershipItem) || [],
        rejectedRequests: data.rejected_requests?.map(transformMembershipItem) || []
      });
      
    } catch (error) {
      console.error("Error fetching membership data:", error);
      setMembershipData({
        newRequests: [],
        waitingPayment: [],
        paymentDone: [],
        activeMemberships: [],
        rejectedRequests: []
      });
    } finally {
      setIsLoadingMemberships(false);
    }
  };

  // --- Approve membership enrollment ---
  const handleApproveMembership = async (enrollmentId, rowId, category) => {
    try {
      setLoadingMembershipAction(`approve-${rowId}`);
      const token = localStorage.getItem('jwttoken');

      const response = await fetch(`https://kaasbackend.vetrinahealthcare.com/lms/membership/enrollment/approve/${enrollmentId}/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Approval failed: ${response.status} - ${JSON.stringify(errorData)}`);
      }

      // Refresh data to update categories
      await fetchMembershipData();
      
      alert(`Membership enrollment #${enrollmentId} approved successfully!`);
      
    } catch (error) {
      console.error("Error approving membership enrollment:", error);
      alert(`Failed to approve membership enrollment: ${error.message}`);
    } finally {
      setLoadingMembershipAction(null);
    }
  }

  // --- Reject membership enrollment ---
  const handleRejectMembership = async (enrollmentId, rowId, category) => {
    try {
      setLoadingMembershipAction(`reject-${rowId}`);
      const token = localStorage.getItem('jwttoken');

      const response = await fetch(`https://kaasbackend.vetrinahealthcare.com/lms/admin/membership/reject/${enrollmentId}/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Rejection failed: ${response.status} - ${JSON.stringify(errorData)}`);
      }

      // Refresh data to update categories
      await fetchMembershipData();
      
      alert(`Membership enrollment #${enrollmentId} rejected successfully!`);
      
    } catch (error) {
      console.error("Error rejecting membership enrollment:", error);
      alert(`Failed to reject membership enrollment: ${error.message}`);
    } finally {
      setLoadingMembershipAction(null);
    }
  }

  // --- Accept / Reject request actions ---
  const handleAccept = async (id) => {
    try {
      setLoadingAction(`accept-${id}`);
      const token = localStorage.getItem('jwttoken');

      await fetch(`https://kaasbackend.vetrinahealthcare.com/lms/enrollment-requests/${id}/approve/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      setPaymentRequests((prev) =>
        prev.map((req) => (req.id === id ? { ...req, status: "Accepted" } : req))
      )
    } catch (error) {
      console.error("Error accepting request:", error);
    }
    finally {
      setLoadingAction(null);
    }
  }

  const handleReject = async (id) => {
    try {
      setLoadingAction(`reject-${id}`);
      const token = localStorage.getItem('jwttoken');

      await fetch(`https://kaasbackend.vetrinahealthcare.com/lms/enrollment-requests/${id}/reject/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      setPaymentRequests((prev) =>
        prev.map((req) => (req.id === id ? { ...req, status: "Rejected" } : req))
      )
    } catch (error) {
      console.error("Error rejecting request:", error);
    }
    finally {
      setLoadingAction(null);
    }
  }

  // --- Handle invoice modal ---
  const handleViewInvoice = (invoice) => {
    setCurrentInvoice(invoice)
    setShowInvoiceModal(true)
  }

  // --- Helper function to get status display info ---
  const getStatusInfo = (status) => {
    const statusLower = status?.toLowerCase() || 'pending';
    
    switch(statusLower) {
      case 'approved':
      case 'active':
        return {
          text: status.charAt(0).toUpperCase() + status.slice(1),
          bgColor: '#e6f7e6',
          textColor: '#4caf50'
        };
      case 'rejected':
        return {
          text: 'Rejected',
          bgColor: '#ffe6e6',
          textColor: '#f44336'
        };
      case 'completed':
      case 'payment_done':
      case 'paid':
        return {
          text: 'Completed',
          bgColor: '#e6f0ff',
          textColor: '#1976d2'
        };
      case 'pending':
      case 'waiting_payment':
        return {
          text: 'Pending',
          bgColor: '#fff8e6',
          textColor: '#ff9800'
        };
      default:
        return {
          text: status || 'Unknown',
          bgColor: '#f5f5f5',
          textColor: '#666'
        };
    }
  };

  // --- Table Columns ---
  const requestColumns = [
    { key: "id", label: "Request ID", sortable: true },
    { key: "user", label: "Student", sortable: true },
    { key: "email", label: "Email", sortable: true },
    { key: "course", label: "Course", sortable: true },
    {
      key: "amount",
      label: "Amount",
      sortable: true,
      render: (row) => <div>₹{row.amount.toFixed(2)}</div>,
    },
    { key: "date", label: "Date", sortable: true },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (row) => {
        const statusInfo = getStatusInfo(row.status);
        return (
          <span
            style={{
              padding: "3px 8px",
              borderRadius: "12px",
              fontSize: "12px",
              backgroundColor: statusInfo.bgColor,
              color: statusInfo.textColor,
              fontWeight: "500",
            }}
          >
            {statusInfo.text}
          </span>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={() => handleAccept(row.id)}
            disabled={row.status !== "Pending" || loadingAction === `accept-${row.id}` || loadingAction === `reject-${row.id}`}
            style={{
              background: "none",
              border: "none",
              color: row.status === "Pending" ? "#4caf50" : "#ccc",
              cursor: row.status === "Pending" ? "pointer" : "not-allowed",
              fontSize: "18px",
              position: "relative",
            }}
          >
            {loadingAction === `accept-${row.id}` ? (
              <div
                className="spinner-border spinner-border-sm text-success"
                role="status"
                style={{ width: "18px", height: "18px" }}
              />
            ) : (
              <FaCheckCircle />
            )}
          </button>

          <button
            onClick={() => handleReject(row.id)}
            disabled={row.status !== "Pending" || loadingAction === `accept-${row.id}` || loadingAction === `reject-${row.id}`}
            style={{
              background: "none",
              border: "none",
              color: row.status === "Pending" ? "#f44336" : "#ccc",
              cursor: row.status === "Pending" ? "pointer" : "not-allowed",
              fontSize: "18px",
              position: "relative",
            }}
          >
            {loadingAction === `reject-${row.id}` ? (
              <div
                className="spinner-border spinner-border-sm text-danger"
                role="status"
                style={{ width: "18px", height: "18px" }}
              />
            ) : (
              <FaTimesCircle />
            )}
          </button>
        </div>
      ),
    }
  ]

  const membershipColumns = (category) => [
    { 
      key: "id", 
      label: "Enrollment ID", 
      sortable: true 
    },
    { 
      key: "userFullName", 
      label: "Student", 
      sortable: true 
    },
    { 
      key: "userEmail", 
      label: "Email", 
      sortable: true 
    },
    { 
      key: "membershipTitle", 
      label: "Membership", 
      sortable: true 
    },
    { 
      key: "membershipType", 
      label: "Type", 
      sortable: true,
      render: (row) => (
        <span style={{
          textTransform: 'capitalize',
          padding: '3px 8px',
          borderRadius: '12px',
          fontSize: '12px',
          backgroundColor: row.membershipType === 'premium' ? '#e6f0ff' : 
                         row.membershipType === 'basic' ? '#e6f7e6' : '#f8f8f8',
          color: row.membershipType === 'premium' ? '#1976d2' : 
                row.membershipType === 'basic' ? '#4caf50' : '#666'
        }}>
          {row.membershipType}
        </span>
      )
    },
    { 
      key: "date", 
      label: "Enrolled Date", 
      sortable: true 
    },
    { 
      key: "paymentDone", 
      label: "Payment", 
      sortable: true,
      render: (row) => (
        <span
          style={{
            padding: "3px 8px",
            borderRadius: "12px",
            fontSize: "12px",
            backgroundColor: row.paymentDone ? "#e6f7e6" : "#fff8e6",
            color: row.paymentDone ? "#4caf50" : "#ff9800",
          }}
        >
          {row.paymentDone ? "Paid" : "Pending"}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (row) => {
        const statusInfo = getStatusInfo(row.status);
        return (
          <span
            style={{
              padding: "3px 8px",
              borderRadius: "12px",
              fontSize: "12px",
              backgroundColor: statusInfo.bgColor,
              color: statusInfo.textColor,
              fontWeight: "500",
              textTransform: 'capitalize'
            }}
          >
            {statusInfo.text}
          </span>
        );
      },
    },
    ...(category === 'newRequests' ? [{
      key: "actions",
      label: "Actions",
      render: (row) => {
        // Only show actions for pending status in newRequests
        const statusLower = row.status?.toLowerCase();
        const isPending = statusLower === 'pending' || statusLower === 'waiting_payment';
        
        return (
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={() => handleApproveMembership(row.enrollmentId, row.id, 'newRequests')}
              disabled={!isPending || (loadingMembershipAction && (loadingMembershipAction === `approve-${row.id}` || loadingMembershipAction === `reject-${row.id}`))}
              style={{
                background: "none",
                border: "none",
                color: isPending ? "#4caf50" : "#ccc",
                cursor: isPending ? "pointer" : "not-allowed",
                fontSize: "18px",
                position: "relative",
                opacity: loadingMembershipAction && (loadingMembershipAction === `approve-${row.id}` || loadingMembershipAction === `reject-${row.id}`) ? 0.6 : 1,
              }}
              title={isPending ? "Approve Membership" : "Already processed"}
            >
              {loadingMembershipAction === `approve-${row.id}` ? (
                <div
                  className="spinner-border spinner-border-sm text-success"
                  role="status"
                  style={{ width: "18px", height: "18px" }}
                />
              ) : (
                <FaCheckCircle />
              )}
            </button>

            <button
              onClick={() => handleRejectMembership(row.enrollmentId, row.id, 'newRequests')}
              disabled={!isPending || (loadingMembershipAction && (loadingMembershipAction === `approve-${row.id}` || loadingMembershipAction === `reject-${row.id}`))}
              style={{
                background: "none",
                border: "none",
                color: isPending ? "#f44336" : "#ccc",
                cursor: isPending ? "pointer" : "not-allowed",
                fontSize: "18px",
                position: "relative",
                opacity: loadingMembershipAction && (loadingMembershipAction === `approve-${row.id}` || loadingMembershipAction === `reject-${row.id}`) ? 0.6 : 1,
              }}
              title={isPending ? "Reject Membership" : "Already processed"}
            >
              {loadingMembershipAction === `reject-${row.id}` ? (
                <div
                  className="spinner-border spinner-border-sm text-danger"
                  role="status"
                  style={{ width: "18px", height: "18px" }}
                />
              ) : (
                <FaTimesCircle />
              )}
            </button>
          </div>
        );
      },
    }] : []),
  ]

  // --- Tab system for membership categories ---
  const [activeMembershipTab, setActiveMembershipTab] = useState("newRequests");

  const getMembershipTabData = () => {
    switch (activeMembershipTab) {
      case "newRequests":
        return {
          data: membershipData.newRequests,
          title: "New Membership Requests",
          description: "Pending membership enrollments waiting for approval"
        };
      case "waitingPayment":
        return {
          data: membershipData.waitingPayment,
          title: "Waiting for Payment",
          description: "Approved memberships waiting for payment"
        };
      case "paymentDone":
        return {
          data: membershipData.paymentDone,
          title: "Payment Completed",
          description: "Memberships with completed payments"
        };
      case "activeMemberships":
        return {
          data: membershipData.activeMemberships,
          title: "Active Memberships",
          description: "Currently active memberships"
        };
      case "rejectedRequests":
        return {
          data: membershipData.rejectedRequests,
          title: "Rejected Requests",
          description: "Membership requests that were rejected"
        };
      default:
        return {
          data: [],
          title: "Membership Enrollments",
          description: ""
        };
    }
  };

  const membershipTabInfo = getMembershipTabData();

  return (
    <div>
      <h1 style={{ marginBottom: "20px", color: "#333" }}>Billing & Payments</h1>

      {/* --- Main Tabs --- */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
        <button
          onClick={() => setActiveTab("requests")}
          style={{
            padding: "10px 20px",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            background:
              activeTab === "requests"
                ? "linear-gradient(135deg, #ff5baf, #7366ff)"
                : "#f1f1f1",
            color: activeTab === "requests" ? "white" : "#333",
            fontWeight: "bold",
          }}
        >
          <FaClipboardList /> Course Requests
        </button>
        <button
          onClick={() => setActiveTab("payments")}
          style={{
            padding: "10px 20px",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            background:
              activeTab === "payments"
                ? "linear-gradient(135deg, #ff5baf, #7366ff)"
                : "#f1f1f1",
            color: activeTab === "payments" ? "white" : "#333",
            fontWeight: "bold",
          }}
        >
          <FaMoneyBillWave /> Membership Management
        </button>
      </div>

      {/* --- Course Requests Table --- */}
      {activeTab === "requests" && (
        <Card title="Course Enrollment Requests">
          <Table columns={requestColumns} data={paymentRequests} pagination={true} itemsPerPage={5} />
        </Card>
      )}

      {/* --- Membership Management --- */}
      {activeTab === "payments" && (
        <Card title={membershipTabInfo.title}>
          {isLoadingMemberships ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p style={{ marginTop: "10px" }}>Loading membership data...</p>
            </div>
          ) : (
            <>
              {/* Membership Category Tabs */}
              <div style={{ display: "flex", gap: "5px", marginBottom: "20px", flexWrap: "wrap" }}>
                <button
                  onClick={() => setActiveMembershipTab("newRequests")}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "6px",
                    border: "none",
                    cursor: "pointer",
                    background: activeMembershipTab === "newRequests" ? "#7366ff" : "#f1f1f1",
                    color: activeMembershipTab === "newRequests" ? "white" : "#333",
                    fontSize: "14px",
                  }}
                >
                  New Requests ({membershipData.newRequests.length})
                </button>
                <button
                  onClick={() => setActiveMembershipTab("waitingPayment")}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "6px",
                    border: "none",
                    cursor: "pointer",
                    background: activeMembershipTab === "waitingPayment" ? "#7366ff" : "#f1f1f1",
                    color: activeMembershipTab === "waitingPayment" ? "white" : "#333",
                    fontSize: "14px",
                  }}
                >
                  Waiting Payment ({membershipData.waitingPayment.length})
                </button>
                <button
                  onClick={() => setActiveMembershipTab("paymentDone")}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "6px",
                    border: "none",
                    cursor: "pointer",
                    background: activeMembershipTab === "paymentDone" ? "#7366ff" : "#f1f1f1",
                    color: activeMembershipTab === "paymentDone" ? "white" : "#333",
                    fontSize: "14px",
                  }}
                >
                  Payment Done ({membershipData.paymentDone.length})
                </button>
                <button
                  onClick={() => setActiveMembershipTab("activeMemberships")}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "6px",
                    border: "none",
                    cursor: "pointer",
                    background: activeMembershipTab === "activeMemberships" ? "#7366ff" : "#f1f1f1",
                    color: activeMembershipTab === "activeMemberships" ? "white" : "#333",
                    fontSize: "14px",
                  }}
                >
                  Active ({membershipData.activeMemberships.length})
                </button>
                <button
                  onClick={() => setActiveMembershipTab("rejectedRequests")}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "6px",
                    border: "none",
                    cursor: "pointer",
                    background: activeMembershipTab === "rejectedRequests" ? "#7366ff" : "#f1f1f1",
                    color: activeMembershipTab === "rejectedRequests" ? "white" : "#333",
                    fontSize: "14px",
                  }}
                >
                  Rejected ({membershipData.rejectedRequests.length})
                </button>
              </div>

              {/* Description */}
              {membershipTabInfo.description && (
                <p style={{ marginBottom: "20px", color: "#666", fontSize: "14px" }}>
                  {membershipTabInfo.description}
                </p>
              )}

              {/* Table */}
              {membershipTabInfo.data.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
                  <FaClipboardList style={{ fontSize: "48px", marginBottom: "10px", opacity: 0.5 }} />
                  <p>No data found in this category.</p>
                </div>
              ) : (
                <Table 
                  columns={membershipColumns(activeMembershipTab)} 
                  data={membershipTabInfo.data} 
                  pagination={true} 
                  itemsPerPage={5} 
                />
              )}
            </>
          )}
        </Card>
      )}

      {/* --- Invoice Modal --- */}
      {showInvoiceModal && currentInvoice && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "10px",
              padding: "20px",
              width: "600px",
              maxWidth: "90%",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h2 style={{ margin: 0 }}>Invoice {currentInvoice.id}</h2>
              <FaFileInvoice style={{ fontSize: "24px", color: "#7366ff" }} />
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
              <div>
                <div style={{ fontWeight: "bold", marginBottom: "5px" }}>Billed To:</div>
                <div>{currentInvoice.user}</div>
                <div>{currentInvoice.email}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: "bold", marginBottom: "5px" }}>Invoice Date:</div>
                <div>{currentInvoice.date}</div>
                <div
                  style={{
                    marginTop: "10px",
                    padding: "3px 8px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    display: "inline-block",
                    backgroundColor:
                      currentInvoice.status === "Paid"
                        ? "#e6f7e6"
                        : currentInvoice.status === "Pending"
                          ? "#fff8e6"
                          : "#ffe6e6",
                    color:
                      currentInvoice.status === "Paid"
                        ? "#4caf50"
                        : currentInvoice.status === "Pending"
                          ? "#ff9800"
                          : "#f44336",
                  }}
                >
                  {currentInvoice.status}
                </div>
              </div>
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "20px" }}>
              <thead>
                <tr style={{ background: "#f9f9f9" }}>
                  <th style={{ padding: "10px", textAlign: "left", borderBottom: "1px solid #ddd" }}>Item</th>
                  <th style={{ padding: "10px", textAlign: "right", borderBottom: "1px solid #ddd" }}>Price</th>
                </tr>
              </thead>
              <tbody>
                {currentInvoice.items.map((item, index) => (
                  <tr key={index}>
                    <td style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>{item.name}</td>
                    <td style={{ padding: "10px", textAlign: "right", borderBottom: "1px solid #ddd" }}>
                      ₹{item.price.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ padding: "10px", fontWeight: "bold", textAlign: "right" }}>Total:</td>
                  <td style={{ padding: "10px", fontWeight: "bold", textAlign: "right" }}>
                    ₹{currentInvoice.amount.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                onClick={() => setShowInvoiceModal(false)}
                style={{
                  padding: "8px 15px",
                  background: "#f5f5f5",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
              <button
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "8px 15px",
                  background: "linear-gradient(135deg, #ff5baf, #7366ff)",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                <FaDownload /> Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BillingPayments